import { getCatalogsByCompany } from '@/lib/api/catalog/catalog-api'
import { useEffect, useRef, useState } from 'react'
import { MdAdd, MdDelete, MdInventory2, MdSearch } from 'react-icons/md'

/**
 * Produto entregue no atendimento, na forma que a tela carrega.
 *
 * Sem preço de venda: o produto está DENTRO do valor do pacote. O que aparece é o
 * fornecedor — que é quem recebe por ele — e a quantidade.
 */
export interface OrderProductItem {
    /** Item de catálogo com `category = PRODUCT` — a mercadoria, desde a unificação. */
    catalogId: number
    name: string
    quantity: number
    /** Só exibição: quem recebe o repasse por este produto. */
    supplierName?: string | null
    unit?: string | null
}

interface Props {
    products: OrderProductItem[]
    onProductsChange: (products: OrderProductItem[]) => void
    companyId: number
}

/**
 * Produtos da VENDA DE PACOTE, na edição do atendimento.
 *
 * Só aparece em venda de pacote: no agendamento comum o produto não faz parte do
 * atendimento (quem vende produto é a loja). Editar aqui não mexe no preço — mexe no
 * CUSTO, porque cada produto vira repasse ao fornecedor.
 */
export function OrderProductsFields({
    products,
    onProductsChange,
    companyId,
}: Props) {
    const [search, setSearch] = useState('')
    const [results, setResults] = useState<
        Array<{ id: number; name: string }>
    >([])
    const [showDropdown, setShowDropdown] = useState(false)
    const [searching, setSearching] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target as Node)
            ) {
                setShowDropdown(false)
            }
        }
        if (showDropdown) document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [showDropdown])

    const handleSearch = (term: string) => {
        setSearch(term)
        if (debounceRef.current) clearTimeout(debounceRef.current)
        if (term.trim().length < 2 || !companyId) {
            setResults([])
            setShowDropdown(false)
            return
        }
        debounceRef.current = setTimeout(async () => {
            setSearching(true)
            try {
                const res = await getCatalogsByCompany(companyId, {
                    category: 'PRODUCT',
                    activeOnly: true,
                    isListing: true,
                    search: term.trim(),
                    limit: 20,
                })
                if (res.success) {
                    setResults(
                        (Array.isArray(res.result) ? res.result : []).filter(
                            p => !products.some(sel => sel.catalogId === p.id)
                        )
                    )
                    setShowDropdown(true)
                }
            } finally {
                setSearching(false)
            }
        }, 300)
    }

    const handleAdd = (product: { id: number; name: string }) => {
        onProductsChange([
            ...products,
            { catalogId: product.id, name: product.name, quantity: 1 },
        ])
        setSearch('')
        setResults([])
        setShowDropdown(false)
    }

    const handleQuantityChange = (index: number, raw: string) => {
        const quantity = Math.max(1, parseInt(raw) || 1)
        onProductsChange(
            products.map((p, i) => (i === index ? { ...p, quantity } : p))
        )
    }

    const handleRemove = (index: number) => {
        onProductsChange(products.filter((_, i) => i !== index))
    }

    return (
        <div className="card bg-base-100 border border-base-300/50 shadow-sm">
            <div className="card-body">
                <h2 className="card-title text-lg mb-1">
                    <MdInventory2 className="text-lg" />
                    Produtos do Pacote
                </h2>
                <p className="text-xs text-base-content/60 mb-4">
                    Entregues junto com o pacote, sem preço próprio — já estão no
                    valor. Cada produto gera repasse ao fornecedor do cadastro.
                </p>

                <div className="relative mb-4" ref={dropdownRef}>
                    <div className="relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-lg pointer-events-none" />
                        <input
                            type="text"
                            className="input input-bordered input-sm w-full pl-10"
                            placeholder="Buscar produto para adicionar..."
                            value={search}
                            onChange={e => handleSearch(e.target.value)}
                        />
                    </div>
                    {showDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                            {searching && (
                                <div className="p-3 text-center text-sm text-base-content/60">
                                    <span className="loading loading-spinner loading-xs mr-2" />
                                    Buscando...
                                </div>
                            )}
                            {!searching && results.length === 0 && (
                                <div className="p-3 text-center text-sm text-base-content/60">
                                    Nenhum produto encontrado.
                                </div>
                            )}
                            {!searching &&
                                results.map(p => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300/50 last:border-b-0 flex items-center gap-2"
                                        onClick={() => handleAdd(p)}
                                    >
                                        <MdAdd className="text-base-content/50" />
                                        <span className="text-sm">{p.name}</span>
                                    </button>
                                ))}
                        </div>
                    )}
                </div>

                {products.length === 0 ? (
                    <p className="text-sm text-base-content/60">
                        Nenhum produto no pacote. Busque acima para adicionar.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Produto</th>
                                    <th className="w-56">Quem recebe</th>
                                    <th className="w-24">Qtd.</th>
                                    <th className="w-12"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((p, index) => (
                                    <tr key={`${p.catalogId}-${index}`}>
                                        <td>
                                            <span className="font-medium text-sm">
                                                {p.name}
                                            </span>
                                            {p.unit && (
                                                <span className="text-xs text-base-content/60 ml-2">
                                                    {p.unit}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-sm">
                                            {p.supplierName || (
                                                /* Produto novo: o fornecedor é o do
                                                   cadastro e só é congelado ao salvar.
                                                   Sem fornecedor lá, o backend recusa. */
                                                <span className="text-base-content/50">
                                                    Fornecedor do cadastro
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                min={1}
                                                className="input input-bordered input-sm w-20"
                                                value={p.quantity}
                                                onChange={e =>
                                                    handleQuantityChange(
                                                        index,
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs text-error"
                                                onClick={() =>
                                                    handleRemove(index)
                                                }
                                                aria-label="Remover produto"
                                            >
                                                <MdDelete />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}
